import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TopNav, Footer } from "@/components/Layout";
import { ArrowRight, ShieldCheck, Leaf, Brain, ClipboardList, BarChart3, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_kissed-lite/artifacts/blw1c1pl_KISSED%20Functional%20Formulas.png";

const Feature = ({ icon: Icon, title, body }) => (
  <div className="kcis-card p-7">
    <div className="h-10 w-10 rounded-xl bg-purple-50 text-purple-900 flex items-center justify-center mb-4">
      <Icon className="h-5 w-5" />
    </div>
    <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav
        right={
          <>
            <Link to="/client/login" className="hidden sm:inline">
              <Button variant="ghost" className="rounded-full text-slate-700 hover:text-purple-900" data-testid="nav-client-login">
                Client Portal
              </Button>
            </Link>
            <Link to="/admin/login">
              <Button variant="ghost" className="rounded-full text-slate-700 hover:text-purple-900" data-testid="nav-admin-login">
                Sign In
              </Button>
            </Link>
            <Link to="/intake">
              <Button className="rounded-full bg-purple-900 hover:bg-purple-800 text-white px-6" data-testid="nav-start-intake">
                Start Assessment
              </Button>
            </Link>
          </>
        }
      />

      {/* Hero */}
      <section className="kcis-hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 kcis-fade-up">
            <div className="kcis-overline text-purple-900 mb-5">KISSED Clinical Intelligence System · KCIS™</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">
              Holistic trichology, <br />
              <span className="text-purple-900">guided by clinical intelligence.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Complete a thoughtful intake. Receive a branded, customized wellness summary and a 12-week
              protocol draft — designed for the modern hair, scalp, and whole-body journey.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/intake">
                <Button className="rounded-full bg-purple-900 hover:bg-purple-800 text-white px-8 py-6 text-base" data-testid="hero-cta-intake">
                  Start Assessment <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/client/login">
                <Button variant="outline" className="rounded-full border-slate-300 text-slate-800 hover:bg-slate-100 px-8 py-6 text-base" data-testid="hero-cta-client">
                  Access My Assessment
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8 text-xs text-slate-500">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-purple-900" /> Holistic wellness education</div>
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-purple-900" /> Personalized wellness insights</div>
            </div>
          </div>

          <div className="lg:col-span-5 kcis-fade-up kcis-delay-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-purple-900/5 rounded-[2rem] blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-gradient-to-br from-white via-purple-50/40 to-white">
                <img
                  src={HERO_IMG}
                  alt="KCIS — KISSED Clinical Intelligence System: holistic trichology guided by clinical intelligence"
                  className="w-full h-[380px] sm:h-[440px] lg:h-[500px] object-contain px-6 sm:px-8 pt-6 sm:pt-8 pb-32"
                />
                <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/85 backdrop-blur-md p-4 border border-white/60">
                  <div className="kcis-overline">Sample protocol</div>
                  <div className="mt-1 font-display text-base font-semibold text-slate-900">12-Week Wellness Path · Personalized</div>
                  <div className="mt-1 text-xs text-slate-600">Hair · Scalp · Stress · Sleep · Hydration</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <div className="kcis-overline">What's inside</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900 mt-3">
            A clinical-grade companion for your wellness practice.
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Feature icon={ClipboardList} title="Thoughtful Intake" body="Capture the whole client — symptoms, hair, scalp, stress, sleep, hydration, goals." />
          <Feature icon={Brain} title="Science-Backed Protocol Report" body="A structured, branded summary across 8 sections, generated in seconds." />
          <Feature icon={Leaf} title="Tea & Service Path" body="Tailored herbal tea suggestions and a recommended in-clinic service journey." />
          <Feature icon={BarChart3} title="Follow-up Tracking" body="A 7-day follow-up captures real progress — energy, sleep, shedding, more." />
        </div>
      </section>

      {/* Disclaimer banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="kcis-card p-8 lg:p-10 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div>
            <div className="kcis-overline text-purple-900">A note on care</div>
            <h3 className="font-display text-2xl text-slate-900 mt-2">Educational wellness support — not medical advice.</h3>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              KCIS™ produces educational, lifestyle-based recommendations. It does not diagnose,
              treat, or replace consultation with a licensed practitioner.
            </p>
          </div>
          <Link to="/intake">
            <Button className="rounded-full bg-purple-900 hover:bg-purple-800 text-white px-7" data-testid="disclaimer-cta-intake">
              Start Assessment <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
